import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Users,
  Download,
  Search,
  Lock,
  Eye,
  EyeOff,
  Mail,
  Phone,
  User,
  RefreshCw,
  CheckCircle,
  XCircle
} from 'lucide-react';
import axios from 'axios';

// Simple Auth Modal Component
const AuthModal = ({
  username,
  password,
  showPassword,
  authError,
  onUsernameChange,
  onPasswordChange,
  onTogglePassword,
  onSubmit,
  onKeyPress
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black bg-opacity-50"></div>
    <div className="relative bg-white rounded-lg shadow-xl p-8 w-full max-w-md mx-4">
      <div className="text-center mb-6">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
          <Lock className="h-6 w-6 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Admin Login</h2>
        <p className="text-gray-600 mt-2">Enter credentials to access participant details</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Username
          </label>
          <input
            type="text"
            value={username}
            onChange={onUsernameChange}
            onKeyPress={onKeyPress}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter username"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={onPasswordChange}
              onKeyPress={onKeyPress}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter password"
            />
            <button
              type="button"
              onClick={onTogglePassword}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-gray-400" />
              ) : (
                <Eye className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {authError && (
          <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded">
            {authError}
          </div>
        )}

        <button
          onClick={onSubmit}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
        >
          Login
        </button>
      </div>
    </div>
  </div>
);

const ParticipantDetails = () => {
  const { eventId } = useParams();

  // Authentication states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Data states
  const [participants, setParticipants] = useState([]);
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Resending state
  const [resendingId, setResendingId] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    const storedAuth = sessionStorage.getItem(`event_admin_auth_${eventId}`);
    if (storedAuth && eventData) {
      const correctAuth = btoa(`${eventData.username}:${eventData.password}`);
      if (storedAuth === correctAuth) {
        setIsAuthenticated(true);
        setShowAuthModal(false);
      } else {
        sessionStorage.removeItem(`event_admin_auth_${eventId}`);
      }
    }
  }, [eventId, eventData]);

  // Fetch event data first
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/events/${eventId}`
        );
        setEventData(response.data);
      } catch (error) {
        console.error('Error fetching event data:', error);
      }
    };
    fetchEventData();
  }, [eventId]);

  // Fetch participants when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchParticipants();
    }
  }, [eventId, isAuthenticated]);

  const fetchParticipants = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/participants/event/${eventId}`
      );
      setParticipants(response.data);
    } catch (error) {
      console.error('Error fetching participants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthentication = () => {
    if (!eventData) {
      setAuthError('Event data not loaded yet');
      return;
    }

    if (
      username === eventData.username &&
      password === eventData.password
    ) {
      const authToken = btoa(`${username}:${password}`);
      sessionStorage.setItem(`event_admin_auth_${eventId}`, authToken);
      setIsAuthenticated(true);
      setShowAuthModal(false);
      setAuthError('');
      setUsername('');
      setPassword('');
    } else {
      setAuthError('Invalid username or password');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAuthentication();
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(`event_admin_auth_${eventId}`);
    setIsAuthenticated(false);
    setShowAuthModal(true);
    setUsername('');
    setPassword('');
    setAuthError('');
    setShowPassword(false);
  };

  // Resend emails and WhatsApp notification
  const handleResendEmails = async (participantId) => {
    if (!window.confirm('Are you sure you want to resend emails and WhatsApp notification to this participant?')) {
      return;
    }

    try {
      setResendingId(participantId);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/participants/${participantId}/resend-emails`
      );
      alert('✅ Emails and WhatsApp notification sent successfully!');
    } catch (error) {
      console.error('Error resending emails:', error);
      alert('❌ Failed to resend: ' + (error.response?.data?.error || error.message));
    } finally {
      setResendingId(null);
    }
  };

  // Filter participants based on search
  const filteredParticipants = participants.filter((p) =>
    `${p.firstName} ${p.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.phone || '').includes(searchTerm)
  );

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Email Status', 'WhatsApp Status'];
    const csvData = filteredParticipants.map((p) => [
      `${p.firstName} ${p.lastName || ''}`.trim(),
      p.email || '',
      `${p.countryCode || ''} ${p.phone || ''}`.trim(),
      p.email ? 'Available' : 'Not Available',
      p.phone ? 'Available' : 'Not Available'
    ]);
    const csvContent = [headers, ...csvData]
      .map((row) => row.map((field) => `"${field}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `${eventData?.eventName || 'event'}_participants.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Authentication Modal */}
      {showAuthModal && (
        <AuthModal
          username={username}
          password={password}
          showPassword={showPassword}
          authError={authError}
          onUsernameChange={(e) => {
            setUsername(e.target.value);
            setAuthError('');
          }}
          onPasswordChange={(e) => {
            setPassword(e.target.value);
            setAuthError('');
          }}
          onTogglePassword={() => setShowPassword(!showPassword)}
          onSubmit={handleAuthentication}
          onKeyPress={handleKeyPress}
        />
      )}

      {/* Main Content */}
      <div className={`${showAuthModal ? 'blur-sm pointer-events-none' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-600" />
                  {eventData?.eventName} - Participants
                </h1>
                <p className="text-gray-600 mt-2">
                  Total Participants: {filteredParticipants.length}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={exportToCSV}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Export CSV
                </button>
                <button
                  onClick={handleLogout}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg font-medium flex items-center gap-2"
                >
                  <Lock className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-full"
              />
            </div>
          </div>

          {/* Participants Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Phone
                    </th>
                    {/* ✅ NEW: Email Sent Column */}
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Email Sent
                    </th>
                    {/* ✅ NEW: WhatsApp Sent Column */}
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      WhatsApp Sent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredParticipants.map((participant, index) => (
                    <tr
                      key={participant._id}
                      className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {participant.firstName} {participant.lastName || ''}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          {participant.email || 'N/A'}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          {participant.countryCode && `+${participant.countryCode}`}{' '}
                          {participant.phone || 'N/A'}
                        </div>
                      </td>

                      {/* ✅ NEW: Email Sent Status */}
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {participant.email ? (
                          <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 mx-auto" />
                        )}
                      </td>
{/* ✅ UPDATED: WhatsApp Sent Status with Number Validation */}
<td className="px-6 py-4 whitespace-nowrap text-center">
  {(() => {
    // Check if phone exists and is not empty
    if (!participant.phone) return <XCircle className="w-5 h-5 text-red-600 mx-auto" />;
    
    // Remove all non-digit characters for validation
    const cleanPhone = participant.phone.replace(/\D/g, '');
    
    // Check if phone has actual digits (at least 6 digits for a valid number)
    const isValidPhone = cleanPhone.length >= 6;
    
    return isValidPhone ? (
      <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
    ) : (
      <XCircle className="w-5 h-5 text-red-600 mx-auto" />
    );
  })()}
</td>

                      {/* Resend Button */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleResendEmails(participant._id)}
                          disabled={resendingId === participant._id}
                          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            resendingId === participant._id
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          {resendingId === participant._id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Sending...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4" />
                              Resend
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredParticipants.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No participants found</p>
                <p className="text-gray-400">
                  Try adjusting your search criteria
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantDetails;
